/**
 * 图片加载优化工具
 *
 * ⚠️ 背景（2026-09-23 实测，务必先读）
 *
 * 曾尝试通过 URL 参数请求压缩图，但实测本项目**三个图片源全部不支持**，
 * 强行加参数服务端返回 400，导致图片无法显示（新用户首次访问会破图）：
 *
 *   ① 阿里云 OSS（test2hongkong.oss-cn-hongkong.aliyuncs.com）
 *      ?x-oss-process=image/resize,w_340/...  →  400 text/plain
 *      （推测：bucket 未开通图片处理，或存在防盗链/鉴权策略）
 *
 *   ② 自有 CDN（cdn.banly.com.cn）
 *      ?width=340&height=604  →  400 text/plain
 *      （width/height 是上传时写死的规格，不是查询参数，传参即报错）
 *
 *   ③ 携程 CDN（dimg*.c-ctrip.com）
 *      _W_340_227_R5_Q70  →  400；而原尺寸 _W_1280_853 → 200
 *      （只允许其预定义尺寸档位，不能任意指定宽高）
 *
 * ── 因此本模块采用「探测 + 开关」策略 ──
 *
 *   · 默认全部关闭 → buildThumbUrl 原样返回，行为与优化前一致，绝对安全
 *   · 提供 probeImageSupport() 运行时探测：真正发起一次带参请求，
 *     只有返回成功（200 且 non-text）才把该域名记为「支持」，之后自动启用压缩
 *   · 也支持通过 localStorage 手动强制开关，便于后端开通后快速验证
 *
 * 后端开通任一能力后（OSS 图片处理 / 缩略图字段 / Nginx image_filter），
 * 前端**无需改代码**即可自动受益；若未开通，则保持原图直连不受影响。
 *
 * ── 后端可选的三条彻底解决路径 ──
 *   A. OSS 控制台开通「图片处理」服务（成本最低）
 *   B. 接口增加缩略图字段（如 cover_image_thumb，最标准）
 *   C. 自建 CDN 上装 ngx_http_image_filter_module 做实时缩放
 */

// ══════════════════════════════════════════════════════════
//  开关与状态
// ══════════════════════════════════════════════════════════

/**
 * 全局总开关
 *
 * - "auto"（默认）：不主动压缩，仅对「探测确认支持」的域名启用
 * - "off"        ：完全禁用压缩，永远原图直连（最保守）
 * - "force"      ：强制对所有域名启用（仅调试用，可能导致破图）
 *
 * 可在控制台动态切换：localStorage.setItem("img_opt_mode", "auto" | "off" | "force")
 */
type OptimizeMode = "auto" | "off" | "force";

function getMode(): OptimizeMode {
  if (typeof window === "undefined") return "auto";
  try {
    const v = localStorage.getItem("img_opt_mode");
    if (v === "off" || v === "force" || v === "auto") return v;
  } catch {
    /* ignore */
  }
  return "auto";
}

/** 已确认支持参数化压缩的域名（探测成功后才加入） */
const supportedHosts = new Set<string>();

/** 探测结果缓存（避免同一域名重复探测） */
const probeCache = new Map<string, Promise<boolean>>();

/** 手动标记某域名支持（后端确认开通后可调用） */
export function markImageHostSupported(host: string): void {
  supportedHosts.add(host);
}

/** 供外部查看当前状态（调试用） */
export function getImageOptimizeState(): {
  mode: OptimizeMode;
  supportedHosts: string[];
} {
  return { mode: getMode(), supportedHosts: [...supportedHosts] };
}

// ══════════════════════════════════════════════════════════
//  域名识别
// ══════════════════════════════════════════════════════════

/** 阿里云 OSS 域名特征 */
function isAliyunOss(host: string): boolean {
  return /\.oss-[a-z0-9-]+\.aliyuncs\.com$/i.test(host);
}

/** 携程图片 CDN 域名特征 */
function isCtrip(host: string): boolean {
  return /(^|\.)c-ctrip\.com$/i.test(host);
}

/** 自有 CDN（cdn.banly.com.cn） */
function isBanlyCdn(host: string): boolean {
  return /(^|\.)banly\.com\.cn$/i.test(host) && host.startsWith("cdn.");
}

/** 判断是否为可改写/可预加载的外部图片地址（排除 base64 / blob） */
export function isTransformable(url: string): boolean {
  if (!url) return false;
  if (url.startsWith("data:")) return false;
  if (url.startsWith("blob:")) return false;
  return /^https?:\/\//i.test(url);
}

/** 安全解析 host */
function getHost(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return "";
  }
}

// ══════════════════════════════════════════════════════════
//  URL 改写（仅在确认支持后才会被调用）
// ══════════════════════════════════════════════════════════

/**
 * 生成「压缩图 URL」的纯函数（不做任何支持性判断）
 *
 * 各域名规则：
 *   · OSS      → ?x-oss-process=image/resize,w_xxx/format,webp/quality,q_80
 *   · 自有 CDN → 改写 width，并按原图比例等比改写 height（避免拉伸变形）
 *   · 携程     → 改写路径中的 _W_宽_高，保留后续参数
 *   · 其他     → 原样返回
 */
function rewriteUrl(
  url: string,
  width: number,
  ratio?: number,
  dpr = 2,
): string {
  if (!isTransformable(url)) return url;

  const physicalW = Math.round(width * dpr);
  const host = getHost(url);
  if (!host) return url;

  // ── ① 阿里云 OSS：resize + 转 WebP + 质量压缩 ──
  //    PNG 转 WebP 后体积可降 90%+，是首屏提速的关键
  if (isAliyunOss(host)) {
    const opts = `image/resize,w_${physicalW}/format,webp/quality,q_80`;
    if (url.includes("x-oss-process=")) {
      return url.replace(/x-oss-process=[^&]*/, `x-oss-process=${opts}`);
    }
    return `${url}${url.includes("?") ? "&" : "?"}x-oss-process=${opts}`;
  }

  // ── ② 自有 CDN：改写 width / height ──
  if (isBanlyCdn(host)) {
    // ★ 关键：height 必须按「原图自身宽高比」等比缩放，
    //   否则只改 width 会导致图片严重拉伸变形。
    const urlW = Number((url.match(/[?&]width=(\d+)/) || [])[1]);
    const urlH = Number((url.match(/[?&]height=(\d+)/) || [])[1]);
    let r: number | undefined;
    if (Number.isFinite(urlW) && Number.isFinite(urlH) && urlW > 0 && urlH > 0) {
      r = urlH / urlW; // 原图真实比例，最可靠
    } else if (ratio && Number.isFinite(ratio) && ratio > 0) {
      r = ratio;
    }
    const physicalH = r ? Math.round(physicalW * r) : undefined;

    let next = url;
    if (/[?&]width=\d+/.test(next)) {
      next = next.replace(/([?&])width=\d+/, `$1width=${physicalW}`);
    } else if (!next.includes("?")) {
      next = `${next}?width=${physicalW}`;
    }
    if (physicalH) {
      if (/[?&]height=\d+/.test(next)) {
        next = next.replace(/([?&])height=\d+/, `$1height=${physicalH}`);
      } else if (next.includes("width=")) {
        next = `${next}&height=${physicalH}`;
      }
    }
    return next;
  }

  // ── ③ 携程 CDN：改写路径中的 _W_宽_高 ──
  if (isCtrip(host)) {
    const r = ratio && Number.isFinite(ratio) && ratio > 0 ? ratio : 0.667;
    const physicalH = Math.round(physicalW * r);
    if (/_W_\d+_\d+/.test(url)) {
      return url.replace(/_W_\d+_\d+/, `_W_${physicalW}_${physicalH}`);
    }
    return url;
  }

  // ── 其他域名：不改写 ──
  return url;
}

// ══════════════════════════════════════════════════════════
//  对外 API
// ══════════════════════════════════════════════════════════

/**
 * 按目标显示宽度生成压缩图 URL
 *
 * 默认（auto 模式）**原样返回** —— 因为实测所有源都不支持参数化压缩，
 * 加参数会 400 破图。只有当某域名被探测/标记为「支持」后，才会真正改写。
 *
 * @param url    原图地址
 * @param width  目标显示宽度（CSS px，非物理像素）
 * @param ratio  高宽比（height / width）
 * @param dpr    目标设备像素比，默认 2
 */
export function buildThumbUrl(
  url: string,
  width = 170,
  ratio?: number,
  dpr = 2,
): string {
  if (!isTransformable(url)) return url;

  const mode = getMode();
  if (mode === "off") return url;

  // auto 模式：只有确认支持的域名才改写
  if (mode === "auto") {
    const host = getHost(url);
    if (!host || !supportedHosts.has(host)) return url;
  }

  return rewriteUrl(url, width, ratio, dpr);
}

/**
 * 生成 srcSet 字符串（1x / 2x 两档）
 *
 * 无法改写时返回 undefined，避免浏览器重复下载同一张原图。
 */
export function buildSrcSet(
  url: string,
  width = 170,
  ratio?: number,
): string | undefined {
  if (!isTransformable(url)) return undefined;
  const one = buildThumbUrl(url, width, ratio, 1);
  const two = buildThumbUrl(url, width, ratio, 2);
  if (one === url && two === url) return undefined; // 未发生改写
  return `${one} 1x, ${two} 2x`;
}

/**
 * 预加载图片（下一页数据到达后提前拉取，滚动到时直接命中缓存）
 *
 * ✅ 不修改 URL 语义（走 buildThumbUrl，未启用压缩时即原图），安全可用。
 */
export function preloadImages(
  urls: string[],
  width = 170,
  ratioOf?: (url: string) => number | undefined,
): void {
  if (typeof window === "undefined") return;

  for (const url of urls) {
    if (!isTransformable(url)) continue;
    const img = new Image();
    // 用 1x 预热即可，2x 由真实渲染按需拉取，避免流量翻倍
    img.src = buildThumbUrl(url, width, ratioOf?.(url), 1);
  }
}

// ══════════════════════════════════════════════════════════
//  运行时探测（可选，用于自动发现后端是否已开通压缩能力）
// ══════════════════════════════════════════════════════════

/**
 * 探测某个图片地址所在域名是否支持参数化压缩
 *
 * 做法：真实发起一次带参请求（探测图很小，开销可忽略）
 *   · 返回 200 且 Content-Type 为图片 → 支持，记入 supportedHosts
 *   · 返回 400 / text/plain / 其他异常   → 不支持，保持原样
 *
 * 探测结果会在本次会话内缓存，同一域名只探一次。
 *
 * @param sampleUrl 该域名下的任意一张真实图片地址
 * @returns 是否支持
 */
export function probeImageSupport(sampleUrl: string): Promise<boolean> {
  if (typeof window === "undefined") return Promise.resolve(false);
  if (!isTransformable(sampleUrl)) return Promise.resolve(false);

  const host = getHost(sampleUrl);
  if (!host) return Promise.resolve(false);

  // 已确认支持 → 直接返回
  if (supportedHosts.has(host)) return Promise.resolve(true);

  // 探测中 → 复用同一个 Promise，避免并发重复请求
  const cached = probeCache.get(host);
  if (cached) return cached;

  // ★ 探测用极小尺寸（w_20），流量开销几乎为 0
  const probeUrl = rewriteUrl(sampleUrl, 20, undefined, 1);

  // 无法改写（说明该域名没有规则）→ 视为不支持
  if (probeUrl === sampleUrl) {
    const r = Promise.resolve(false);
    probeCache.set(host, r);
    return r;
  }

  const promise = new Promise<boolean>((resolve) => {
    const img = new Image();
    const finish = (ok: boolean) => {
      if (ok) supportedHosts.add(host);
      resolve(ok);
    };
    img.onload = () => finish(img.naturalWidth > 0);
    img.onerror = () => finish(false);
    // 保险：8 秒未响应视为不支持
    window.setTimeout(() => finish(false), 8000);
    img.src = probeUrl;
  });

  probeCache.set(host, promise);
  return promise;
}

/**
 * 批量探测一组图片地址涉及的域名
 *
 * 用法（后端开通 OSS 图片处理后，可在首页调用一次即可全站受益）：
 *   import { probeImageSupport } from "@/utils/image";
 *   probeImageSupport(list[0].cover_image);
 */
export async function probeImageSupportBatch(
  urls: string[],
): Promise<Record<string, boolean>> {
  const hosts = [...new Set(urls.map(getHost).filter(Boolean))];
  const entries = await Promise.all(
    hosts.map(async (h) => {
      const sample = urls.find((u) => getHost(u) === h) || "";
      const ok = await probeImageSupport(sample);
      return [h, ok] as const;
    }),
  );
  return Object.fromEntries(entries);
}
